















import Vue from 'vue'
import * as R from 'ramda'
import LivePatientStatus from '@/components/live-dashboard/LivePatientStatus.vue'
import { LIVE_DASHBOARD_TYPES } from '@/constants/live-dashboard'
import { getPatientName } from '@/utils/patients'
import { VitalSignReading } from '@/jsonapi/PatientExercise'
import { DataPoint } from '@/jsonapi/CommonTypes'
import { DataPointStages } from '@/constants/exercise-program'

const enum DataPointStageIndexes {
  beginning = 0,
  middle = 1,
  end = 2,
}

interface FlatDataPoint {
  pulseRate: number
  oxygenSaturation: number
  timestamp: number
}

type ComponentData = {
  beginningPatchCounter: number
  middlePatchCounter: number
  endPatchCounter: number
  beginningDataPoints: FlatDataPoint[]
  middleDataPoints: FlatDataPoint | null
  endDataPoints: FlatDataPoint[]
}

export default Vue.extend({
  components: {
    LivePatientStatus,
  },
  props: {
    livePatientWithHistories: {
      type: Object,
      required: true,
    },
    patchInterval: {
      type: Number,
      default: 3,
      validator(val) {
        return val >= 1
      },
    },
  },
  data(): ComponentData {
    return {
      beginningPatchCounter: 1,
      middlePatchCounter: 1,
      endPatchCounter: 1,
      beginningDataPoints: [],
      middleDataPoints: {
        timestamp: null,
        pulseRate: null,
        oxygenSaturation: null,
      },
      endDataPoints: [],
    }
  },
  computed: {
    cards(): Array<{ key: LIVE_DASHBOARD_TYPES; data: DataPoint[] }> {
      return [
        { key: LIVE_DASHBOARD_TYPES.PulseRates, data: this.livePatientWithHistories.pulseRates },
        { key: LIVE_DASHBOARD_TYPES.OxygenSaturations, data: this.livePatientWithHistories.oxygenSaturations },
      ]
    },
    patientName(): string {
      return getPatientName({
        firstName: this.livePatientWithHistories.firstName,
        lastName: this.livePatientWithHistories.lastName,
        id: this.livePatientWithHistories.id,
      })
    },
    patientId(): string {
      return this.livePatientWithHistories.id
    },
    //exercise session specific data
    flatBeginningDataPoint() {
      return this.getFlatDataPoint(DataPointStageIndexes.beginning)
    },
    flatMiddleDataPoint() {
      return this.getFlatDataPoint(DataPointStageIndexes.middle)
    },
    flatEndDataPoint() {
      return this.getFlatDataPoint(DataPointStageIndexes.end)
    },
  },
  watch: {
    flatBeginningDataPoint: {
      deep: true,
      handler(newVal, oldVal) {
        this.handleDataPointChanges(newVal, oldVal, DataPointStages.beginning)
      },
    },
    flatMiddleDataPoint: {
      deep: true,
      handler(newVal, oldVal) {
        this.handleDataPointChanges(newVal, oldVal, DataPointStages.middle)
      },
    },
    flatEndDataPoint: {
      deep: true,
      handler(newVal, oldVal) {
        this.handleDataPointChanges(newVal, oldVal, DataPointStages.end)
      },
    },
  },
  mounted() {
    ;[DataPointStages.beginning, DataPointStages.middle, DataPointStages.end].forEach(
      stage => (this[`${stage}PatchCounter`] = this.patchInterval)
    )
  },
  methods: {
    getFlatDataPoint(index: number): FlatDataPoint {
      const { oxygenSaturations, pulseRates } = this.livePatientWithHistories
      if (!oxygenSaturations[index] || !pulseRates[index]) {
        return {
          oxygenSaturation: null,
          pulseRate: null,
          timestamp: null,
        }
      } else {
        return {
          oxygenSaturation: oxygenSaturations[index].value,
          pulseRate: pulseRates[index].value,
          timestamp: pulseRates[index].timestamp,
        }
      }
    },
    buildVitalSignReading(sessionStage): VitalSignReading {
      const dataPoints = this[`${sessionStage}DataPoints`]
      if (sessionStage === DataPointStages.middle) {
        return {
          timestamp: dataPoints.timestamp,
          heartRateBpm: dataPoints.pulseRate,
          bloodOxygenPerc: dataPoints.oxygenSaturation,
          borgScale: null,
          o2Lpm: null,
        }
      } else {
        return {
          timestamp: dataPoints[0].timestamp,
          heartRateBpm: R.mean(dataPoints.map(dp => dp.pulseRate)),
          bloodOxygenPerc: R.mean(dataPoints.map(dp => dp.oxygenSaturation)),
          borgScale: null,
          o2Lpm: null,
        }
      }
    },
    handleDataPointChanges(newVal, oldVal, sessionStage) {
      if (newVal.timestamp !== oldVal.timestamp) {
        if (sessionStage === DataPointStages.middle) {
          console.log(`${newVal.pulseRate} vs ${this.middleDataPoints.pulseRate}`)
          if (newVal.pulseRate > this.middleDataPoints.pulseRate) {
            console.log('updating middleDataPoints')
            this.middleDataPoints = newVal
          }
        } else {
          this[`${sessionStage}DataPoints`].push(newVal)
        }
        if (this[`${sessionStage}PatchCounter`] >= this.patchInterval) {
          const vitalSignReadings = []
          if (!R.isEmpty(this[`${DataPointStages.beginning}DataPoints`])) {
            vitalSignReadings.push(this.buildVitalSignReading(DataPointStages.beginning))
          }
          if (this[`${DataPointStages.middle}DataPoints`].timestamp) {
            vitalSignReadings.push(this.buildVitalSignReading(DataPointStages.middle))
          }
          if (!R.isEmpty(this[`${DataPointStages.end}DataPoints`])) {
            vitalSignReadings.push(this.buildVitalSignReading(DataPointStages.end))
          }
          this.$emit('patchPatientExercise', {
            patientId: this.patientId,
            sessionStage: sessionStage,
            vitalSignReadings,
          })
          this[`${sessionStage}PatchCounter`] = 1
        } else {
          this[`${sessionStage}PatchCounter`] += 1
        }
      }
    },
  },
})
